import React, { useRef, useEffect, useState } from "react";

interface SiderailProps {
  children: React.ReactNode;
  collapsedContent?: React.ReactNode;
  stickyTriggerSelector: string;
  stickyStopSelector: string;
  className?: string;
  collapsedClassName?: string;
  initialRight?: number;
  initialTop?: number;
}

function Siderail({
  children,
  collapsedContent,
  stickyTriggerSelector,
  stickyStopSelector,
  className = "",
  collapsedClassName = "",
  initialRight = 0,
  initialTop = 0,
}: SiderailProps) {
  const siderailRef = useRef<HTMLDivElement>(null);
  const [stickyState, setStickyState] = useState<
    "initial" | "sticky" | "stopped"
  >("initial");
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [initialPosition, setInitialPosition] = useState({ top: 0, left: 0 });
  const [stopPosition, setStopPosition] = useState(0);
  const [fixedRightValue, setFixedRightValue] = useState(0);

  useEffect(() => {
    const siderail = siderailRef.current;
    if (!siderail) return;

    // This accounts for potential scrollbar width correction value when the sidebar becomes fixed
    let correctionOffset = 15;

    // Try to calculate scrollbar width if possible as a potential source of the offset
    const scrollbarWidth =
      typeof window !== "undefined"
        ? window.innerWidth - document.documentElement.clientWidth
        : 0;

    if (scrollbarWidth > 0) {
      correctionOffset = scrollbarWidth;
    }

    // Get and store initial dimensions and position
    const updateInitialPositions = () => {
      const rect = siderail.getBoundingClientRect();
      setInitialPosition({
        top: rect.top + window.scrollY,
        left: rect.left,
      });
      setFixedRightValue(window.innerWidth - rect.right - correctionOffset);
    };

    updateInitialPositions();

    const handleScroll = () => {
      if (!siderail) return;

      const stickyTrigger = document.querySelector(stickyTriggerSelector);
      const stickyStop = document.querySelector(stickyStopSelector);

      if (!stickyTrigger || !stickyStop) {
        console.error(
          `Bound elements not found for : ${!stickyTrigger ? stickyTriggerSelector : stickyStopSelector}`,
        );
        return;
      }

      // Get current positions
      const triggerRect = stickyTrigger.getBoundingClientRect();
      const stopRect = stickyStop.getBoundingClientRect();
      const siderailRect = siderail.getBoundingClientRect();

      // Use initialTop as the offset when sticky
      const headerOffset = initialTop;

      // Calculate where the siderail would stop
      const siderailHeight = siderailRect.height;
      const stopTop = stopRect.top + window.scrollY - siderailHeight;
      setStopPosition(stopTop);

      const triggerTop = triggerRect.top + window.scrollY;
      const scrolledToTrigger = window.scrollY >= triggerTop - headerOffset;

      const reachedStop = window.scrollY >= stopTop - headerOffset;

      if (reachedStop) {
        setStickyState("stopped");
      } else if (scrolledToTrigger) {
        setStickyState("sticky");
      } else {
        setStickyState("initial");
      }

      // If stickyState is 'sticky' or 'stopped', then isCollapsed should be true
      setIsCollapsed(scrolledToTrigger || reachedStop);
    };

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", () => {
      updateInitialPositions();
      handleScroll();
    });

    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", updateInitialPositions);
    };
  }, [stickyTriggerSelector, stickyStopSelector, initialRight, initialTop]);

  useEffect(() => {
    // This effect ensures isCollapsed is always in sync with stickyState
    setIsCollapsed(stickyState !== "initial");
  }, [stickyState]);

  const style = {
    top:
      stickyState === "sticky"
        ? `${initialTop}px`
        : stickyState === "stopped"
          ? `${stopPosition - initialPosition.top + initialTop}px`
          : `${initialTop}px`,
    right:
      stickyState === "sticky"
        ? `${fixedRightValue}px` // Use the exact distance from viewport right edge
        : `${initialRight}px`, // Use initial right value for absolute positioning
  };

  return (
    <div
      ref={siderailRef}
      className={`
        ${stickyState === "initial" ? "absolute" : stickyState === "sticky" ? "fixed" : "absolute"}
        ${className}
        ${isCollapsed ? collapsedClassName : ""}
      `}
      style={style}
    >
      {isCollapsed && collapsedContent ? collapsedContent : children}
    </div>
  );
}

export default Siderail;
